import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useVisualMode } from '../../utils/visualMode';
import { FaStar, FaRegStar } from 'react-icons/fa';
import ResponsiveButton from '../ResponsiveButton/NeonButton.jsx';

import { absolutize, getProfileSrc, LOCAL_BAND_PLACEHOLDER } from '../../utils/media.js';

/* ---------------- helpers ---------------- */
const getYouTubeId = (url = '') => {
  const s = String(url).trim();
  if (!s) return null;
  let m = s.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  m = s.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  m = s.match(/\/shorts\/([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  m = s.match(/embed\/([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};

const prettyType = (raw) => {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  if (/(trub|truba)/.test(s)) return 'Trubaduuri';
  if (/duo/.test(s)) return 'Duo';              // 👈 kaikki duot → "Duo"
  if (/(bile|bändi|band)/.test(s)) return 'Bilebändi';
  if (/dj/.test(s)) return 'DJ';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const firstYouTubeIdFromMultiline = (raw = '') =>
  String(raw)
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map(getYouTubeId)
    .find(Boolean) || null;

/** ---------------------------------------------------------------
 * ydin-apuri: array | object[] | string(,,-/JSON/rivinvaihdot) -> string[]
 * - object[]: käyttää name/region/title/label -kenttiä
 * - string: ymmärtää JSON-arrayn, rivinvaihdot ja pilkut
 * --------------------------------------------------------------*/
const toStringArray = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) {
    return val
      .map((x) =>
        typeof x === 'object' && x
          ? (x.label ?? x.name ?? x.region ?? x.title ?? '')
          : String(x)
      )
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const s0 = String(val).trim();
  if (!s0) return [];
  // jos JSON-taulukko
  if (s0.startsWith('[') && s0.endsWith(']')) {
    try {
      const j = JSON.parse(s0);
      if (Array.isArray(j)) return toStringArray(j);
    } catch {}
  }
  // rivinvaihdot/pilkut
  return s0.replace(/\n+/g, ',').split(',').map((s) => s.trim()).filter(Boolean);
};

const toCommaString = (val) => toStringArray(val).join(', ');

/* ---------------- component ---------------- */
const ArtistCard = ({
  band,
  onViewProfile,
  highlight,
  badgeText = 'Kuukauden esiintyjä',
  enableVideo = true,
}) => {
  const { isMinimal } = useVisualMode();
  const SHOW_TYPE_ROW = false;

  // Video
  const videoId = useMemo(() => {
    const candidate =
      band.first_video_url ||
      band.video_link ||
      (Array.isArray(band.videos) && band.videos[0]?.url) ||
      '';
    return firstYouTubeIdFromMultiline(candidate);
  }, [band.first_video_url, band.video_link, band.videos]);

  const videoSrc = useMemo(
    () =>
      videoId
        ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&playsinline=1&rel=0`
        : null,
    [videoId]
  );

  // Näyttöleveys & reduced motion – useState + useEffect (SSR-ystävällinen)
  const [desktop, setDesktop] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);

  const mediaRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [imgSrc, setImgSrc] = useState(getProfileSrc(band));
  // Dynaaminen kuvan painopiste (object-position)
  const [objectPos, setObjectPos] = useState('50% 40%');
  const [hovered, setHovered] = useState(false);
  const [videoMounted, setVideoMounted] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [cooldown, setCooldown] = useState(null);

  // ennen returnia:
  const resolvedImg = useMemo(() => {
    if (!imgSrc) return LOCAL_BAND_PLACEHOLDER;               // tyhjä -> paikallinen placeholder
    if (imgSrc.startsWith('/delva-media/')) return imgSrc;    // public-kansion asset, älä absolutisoi
    return absolutize(imgSrc);                                // palvelimen media -> absolutisoi
  }, [imgSrc]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.log('ArtistCard image debug', {
      slug: band.slug,
      rawProfile: band.profile_image || band.profile_image_url || band.profile_image_override,
      imgSrc,
      resolvedImg,
    });
  }, [band, imgSrc, resolvedImg]);

  // Päätä kuvan object-position luonnollisen koon perusteella (tai band.image_focus_y)
  const handleImgLoad = (e) => {
    const img = e.currentTarget;
    const w = img.naturalWidth || 0;
    const h = img.naturalHeight || 0;
    if (!w || !h) return;

// TESTI: vähän alemmaksi, esim. 28 %
setObjectPos('50% 15%');
return;

  };

  // Päivitä desktop/reducedMotion matchMedia:lla
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mqDesktop = window.matchMedia('(min-width:1024px)');
    const mqReduce  = window.matchMedia('(prefers-reduced-motion: reduce)');

    const update = () => {
      setDesktop(mqDesktop.matches);
      setReducedMotion(mqReduce.matches);
    };

    update();

    // addEventListener jos on olemassa, fallback addListeneriin
    if (typeof mqDesktop.addEventListener === 'function') {
      mqDesktop.addEventListener('change', update);
      mqReduce.addEventListener('change', update);
    } else if (typeof mqDesktop.addListener === 'function') {
      mqDesktop.addListener(update);
      mqReduce.addListener(update);
    }

    return () => {
      if (typeof mqDesktop.removeEventListener === 'function') {
        mqDesktop.removeEventListener('change', update);
        mqReduce.removeEventListener('change', update);
      } else if (typeof mqDesktop.removeListener === 'function') {
        mqDesktop.removeListener(update);
        mqReduce.removeListener(update);
      }
    };
  }, []);

  const canPlayVideo = enableVideo && !!videoId && desktop && !reducedMotion;

  useEffect(() => {
    if (!canPlayVideo || !mediaRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.25 }
    );
    io.observe(mediaRef.current);
    return () => io.disconnect();
  }, [canPlayVideo]);

  useEffect(() => {
    setImgSrc(getProfileSrc(band));
  }, [band]); // ok tähän tarkoitukseen

  useEffect(() => {
    if (!(canPlayVideo && inView)) return;
    if (hovered && !videoMounted) {
      const t = setTimeout(() => setVideoMounted(true), 120);
      return () => clearTimeout(t);
    }
  }, [hovered, canPlayVideo, inView, videoMounted]);

  const onMediaEnter = () => {
    setHovered(true);
    if (cooldown) {
      clearTimeout(cooldown);
      setCooldown(null);
    }
  };
  const onMediaLeave = () => {
    setHovered(false);
    const t = setTimeout(() => {
      setVideoMounted(false);
      setVideoReady(false);
    }, 200);
    setCooldown(t);
  };
  useEffect(() => () => cooldown && clearTimeout(cooldown), [cooldown]);

  useEffect(() => {
    setVideoMounted(false);
    setVideoReady(false);
  }, [videoId]);

  const showIframe = canPlayVideo && inView && hovered && videoMounted;

  // Tekstit
  const regionsText =
    toCommaString(band.performance_regions ?? band.regions) ||
    toCommaString(band.performance_regions_string);

  // Genret tageiksi
  const genresArr = useMemo(
    () => toStringArray(band.genres ?? band.genre ?? band.genre_string),
    [band.genres, band.genre, band.genre_string]
  );

  const typeLabel = prettyType(
    band.artist_type_name ?? band.artist_type ?? band.type ?? ''
  );

  // koko kortti vie profiiliin
  const goToProfile = () => {
    onViewProfile?.(band.slug);
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 100);
  };

  const onKeyActivate = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToProfile();
    }
  };

  // Arviot (puolikkaat tähdet)
  const { avg, full, half, empty, reviewsCount, hasReviews } = useMemo(() => {
    const r = Number(band.average_rating ?? band.rating?.average);
    const a = Number.isFinite(r) ? Math.min(5, Math.max(0, r)) : 0;
    const c0 = Number(band.reviews_count ?? band.rating?.total);
    const c = Number.isFinite(c0) ? Math.max(0, Math.floor(c0)) : 0;

    const full = Math.floor(a);
    const half = a - full >= 0.5 ? 1 : 0;
    const empty = 5 - full - half;

    return { avg: a, full, half, empty, reviewsCount: c, hasReviews: c > 0 };
  }, [band.average_rating, band.reviews_count, band.rating]);

  // debug – näet konsolista mitä kortti saa (devissä)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.debug('ArtistCard v2 snapshot:', {
      instrument_labels: band?.instrument_labels,
      instruments: band?.instruments,
      instruments_string: band?.instruments_string,
      additional_service_labels: band?.additional_service_labels,
      additional_services: band?.additional_services,
      additional_services_string: band?.additional_services_string,
    });
  }, [band]);

  return (
<div
      className={`group relative overflow-hidden flex flex-col rounded-2xl bg-white/40 dark:bg-night-800/50 backdrop-blur-md border border-white/10 dark:border-white/5 shadow-lg hover:shadow-neon transition-transform duration-300 ${
        isMinimal ? '' : 'hover:scale-[1.03]'
      } focus-within:shadow-neon-primary cursor-pointer`}
      tabIndex={0}
      onClick={goToProfile}
      onKeyDown={onKeyActivate}
    >
      {/* sheen + neon ring */}
      <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/5 dark:ring-white/10 transition-shadow duration-500 group-hover:shadow-[0_0_24px_rgba(49,255,217,0.35),0_0_48px_rgba(122,0,255,0.25)]" />
      <span className="pointer-events-none absolute -left-1/3 top-0 h-full w-2/3 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-[-120%] group-hover:translate-x-[180%] transition-transform duration-[1200ms] ease-out rounded-2xl" />

      {highlight && (
        <div className="absolute top-2 left-2 z-10 px-3 py-1 rounded-full text-white text-[13px] font-semibold tracking-wide bg-gradient-to-r from-pink to-secondary shadow-neon-pink">
          <span className="inline-flex items-center gap-1">
            <FaStar className="drop-shadow-sm" />
            {badgeText}
          </span>
        </div>
      )}

      {/* Media */}
      <div
        ref={mediaRef}
        className="relative w-full aspect-[16/9] lg:aspect-[16/9] overflow-hidden rounded-t-2xl"
        onMouseEnter={onMediaEnter}
        onMouseLeave={onMediaLeave}
      >
        <img
          src={resolvedImg}
          alt={band?.name || 'Esiintyjä'}
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${
            isMinimal ? '' : 'group-hover:scale-[1.03]'
          }`}
          style={{ objectPosition: objectPos }}
          onLoad={handleImgLoad}
          onError={() => setImgSrc('')}
        />

        {showIframe && videoSrc && (
          <iframe
            className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500 ${
              videoReady ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setVideoReady(true)}
            src={videoSrc}
            title={`${band.name} taustavideo`}
            frameBorder="0"
            loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture"
            aria-hidden="true"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/15 to-transparent" />
      </div>

      {/* Tyypin badge (oikea ylä) */}
      {typeLabel && (
        <span
          className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-black/80 text-xs font-semibold tracking-wide bg-gradient-to-r from-electric to-primary border border-white/20 shadow-neon-primary"
          aria-label={`Tyyppi: ${typeLabel}`}
          title={`Tyyppi: ${typeLabel}`}
        >
          {typeLabel}
        </span>
      )}

      {/* Sisältö */}
      <div className="p-4 flex flex-col flex-grow">
        <h3 className="font-customFont2 dark:text-shadow-lg text-left text-black/80 dark:text-primary text-2xl font-thin leading-[1.2]">
          {band.name}
        </h3>

        {/* Musiikkityylit (tekstinä kuten Esiintymisalueet) */}
        {genresArr.length > 0 && (
          <p className="font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-primary mt-1">
            <span className="dark:text-electric dark:text-shadow-lg font-extrabold mr-1">
              Musiikkityylit:
            </span>
            {genresArr.join(', ')}
          </p>
        )}

        {/* Esiintymisalueet */}
        {regionsText && (
          <p className="font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-primary mt-1">
            <span className="dark:text-electric dark:text-shadow-lg font-extrabold mr-1">
              Esiintymisalueet:
            </span>
            {regionsText}
          </p>
        )}

        {/* (valinnainen) Esiintyjätyyppi rivinä */}
        {SHOW_TYPE_ROW && typeLabel && (
          <p className="font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-primary">
            <span className="dark:text-electric dark:text-shadow-lg font-bold mr-1">
              Esiintyjätyyppi:
            </span>
            {typeLabel}
          </p>
        )}

        {/* Paikkakunta */}
        {band.location && (
          <p className="font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-primary">
            <span className="dark:text-electric dark:text-shadow-lg font-bold mr-1">
              Paikkakunnalta:
            </span>{' '}
            {band.location}
          </p>
        )}

        {/* Arvostelut */}
        {hasReviews ? (
          <div
            className="flex items-center my-4"
            aria-label={`Arvosana ${avg.toFixed(1)} / 5, ${reviewsCount} arvostelua`}
            role="img"
          >
            <span className="font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm text-black/80 dark:text-primary">
              {avg.toFixed(1)} / 5
            </span>
            <div className="ml-2 flex">
              {Array.from({ length: full }).map((_, i) => (
                <FaStar
                  key={`f${i}`}
                  className="text-yellow-400 drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]"
                />
              ))}
              {half ? (
                <span
                  key="half"
                  className="relative inline-block"
                  style={{ width: '1em', height: '1em' }}
                >
                  <FaRegStar className="text-gray-400 absolute inset-0" />
                  <FaStar
                    className="text-yellow-400 absolute inset-0"
                    style={{ clipPath: 'inset(0 50% 0 0)' }}
                  />
                </span>
              ) : null}
              {Array.from({ length: empty }).map((_, i) => (
                <FaRegStar key={`e${i}`} className="text-gray-400" />
              ))}
            </div>
            <span className="font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-bold ml-3 text-black/80 dark:text-primary">
              ({reviewsCount})
            </span>
          </div>
        ) : (
          <p className="font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-bold my-4 text-black/80 dark:text-primary">
            Ei vielä arvosteluja
          </p>
        )}

        <div className="mt-auto flex gap-3 justify-center flex-wrap">
          <ResponsiveButton
            variant="secondary"
            className="w-full scale-[0.80] md:scale-100 font-semibold"
            onClick={(e) => {
              e.stopPropagation(); // estä kortin onClick → ei tuplanavigointia
              goToProfile();
            }}
          >
            VARAA HETI!
          </ResponsiveButton>
        </div>
      </div>
    </div>
  );
};

ArtistCard.propTypes = {
  band: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    slug: PropTypes.string,
    name: PropTypes.string,
    profile_image: PropTypes.string,
    performance_regions: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    performance_regions_string: PropTypes.string,
    regions: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    location: PropTypes.string,
    genres: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    genre: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    genre_string: PropTypes.string,

    sets: PropTypes.array,
    instruments: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    instrument_labels: PropTypes.array,
    instruments_string: PropTypes.string,
    additional_services: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    additional_service_labels: PropTypes.array,
    additional_services_string: PropTypes.string,

    reviews_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    average_rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    video_link: PropTypes.string,
    first_video_url: PropTypes.string,
    videos: PropTypes.array,
    artist_type: PropTypes.string,
    artist_type_name: PropTypes.string,
    type: PropTypes.string,
    // valinnainen manuaalifokus (0–100, y-akseli)
    image_focus_y: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onViewProfile: PropTypes.func,
  highlight: PropTypes.bool,
  badgeText: PropTypes.string,
  enableVideo: PropTypes.bool,
};

export default ArtistCard;
